import type { CSSProperties, ReactNode } from "react"
import type { SectionProps } from "../types"
import type { Organization } from "../types"
import { Button } from "@/components/ui/button"
import { ScrollIndicator } from "../components/ScrollIndicator"
import { resolveHeroMinHeight, resolveScrollArrow } from "@/components/blocks/render/shared"
import type { BlockStyleOverrides } from "@/components/website-builder/blocks/types"

function resolveElStyle(elStyles: Record<string, unknown> | undefined, key: string): CSSProperties {
  if (!elStyles) return {}
  const s = elStyles[key] as BlockStyleOverrides | undefined
  if (!s) return {}
  const result: CSSProperties = {}
  if (s.text_color) result.color = s.text_color
  if (s.text_align) result.textAlign = s.text_align
  return result
}

interface HeroSectionProps extends SectionProps {
  org?: Organization | null
  /** When true (builder context), suppress overlay and use minHeight 380 to avoid double gradient */
  inBuilder?: boolean
  /** NOS-236: page context for the scroll-arrow default (on for homepage, off elsewhere). */
  isHomePage?: boolean
}

const DEFAULT_OVERLAY_OPACITY = 0.55

export function HeroSection({
  content,
  customizations,
  org,
  inBuilder = false,
  styleOverrides,
  isHomePage,
}: HeroSectionProps) {
  const logoHidden = (content?.logo_hidden || content?.logo_deleted) as boolean | undefined
  const headlineDeleted = (content?.headline_deleted || content?.headline_hidden) as boolean | undefined
  const subheadlineDeleted = (content?.subheadline_deleted || content?.subheadline_hidden) as boolean | undefined
  const ctaDeleted = (content?.cta_text_deleted || content?.cta_text_hidden) as boolean | undefined
  const headline =
    (content?.headline as string) ??
    org?.name ??
    "Your impact starts here"
  const subheadline =
    (content?.subheadline as string) ??
    (org?.mission_statement as string) ??
    "Empowering communities through sustainable solutions."
  const ctaText = (content?.cta_text as string) ?? "Donate now"
  const ctaUrl = (content?.cta_url as string) || "#donate"
  const backgroundImage = content?.background_image as string | undefined
  const backgroundImageAlt = (content?.background_image_alt as string) ?? ""
  const overlayOpacity = Math.min(
    1,
    Math.max(0, (content?.background_overlay_opacity as number) ?? DEFAULT_OVERLAY_OPACITY)
  )
  const ariaLabel = backgroundImage
    ? (backgroundImageAlt || "Hero section background")
    : undefined

  const sectionFontFamily = customizations?.font_family ?? "var(--font-family, Inter, sans-serif)"

  return (
    <section
      className={`relative flex flex-col items-center justify-center px-4 py-20 text-center @sm:px-6 @md:px-8 ${styleOverrides?.className ?? ""}`.trim()}
      style={{
        fontFamily: sectionFontFamily,
        ...(backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}),
        minHeight: resolveHeroMinHeight(content, inBuilder, "100vh", 520),
        ...(styleOverrides?.style && Object.keys(styleOverrides.style).length > 0 ? styleOverrides.style : {}),
      }}
      role={backgroundImage ? "img" : undefined}
      aria-label={backgroundImage ? ariaLabel : undefined}
    >
      {/* NOS-7: Dark overlay for contrast when background_image, else brand gradient.
          The section always paints its own background — builder and public render identically. */}
      <div
        className="absolute inset-0"
        style={{
          background: backgroundImage
            ? `rgba(0,0,0,${overlayOpacity})`
            : "linear-gradient(135deg, var(--nos-color-primary, #3b82f6) 0%, var(--nos-color-accent, var(--nos-color-secondary, #1e40af)) 100%)",
        }}
        aria-hidden
      />
      <div className="relative z-10 flex max-w-4xl flex-col items-center">
        {!logoHidden && (org?.logo_url ? (
          <img
            src={org.logo_url}
            alt={org.name ?? "Organization logo"}
            className="h-12 w-auto object-contain drop-shadow-md @md:h-16"
          />
        ) : (
          <div
            className="flex h-12 w-20 items-center justify-center rounded bg-white/10 text-xl font-bold text-white drop-shadow @md:h-16 @md:w-24 @md:text-2xl"
            aria-hidden
          >
            {org?.name?.charAt(0) ?? "?"}
          </div>
        ))}
        {(() => {
          const order = (content?.element_order as string[] | undefined) ?? ["headline", "subheadline", "cta_text"]
          const elStyles = content?._el_styles as Record<string, unknown> | undefined
          const map: Record<string, ReactNode> = {}
          if (!headlineDeleted) map["headline"] = (
            <h1 key="headline" className="mt-4 text-4xl font-bold tracking-tight text-white drop-shadow-lg @sm:text-5xl @md:text-6xl @lg:text-7xl" style={resolveElStyle(elStyles, "headline")}>
              {headline}
            </h1>
          )
          if (!subheadlineDeleted) map["subheadline"] = (
            <p key="subheadline" className="mt-4 max-w-2xl text-lg leading-relaxed text-white/95 drop-shadow @sm:text-xl @md:text-2xl" style={resolveElStyle(elStyles, "subheadline")}>
              {subheadline}
            </p>
          )
          if (!ctaDeleted) map["cta_text"] = (
            <Button
              key="cta_text"
              size="lg"
              className="mt-8 min-h-[48px] min-w-[180px] px-8 text-base font-semibold shadow-lg transition-all hover:scale-105 hover:opacity-90 @sm:min-h-[52px] @sm:px-10 @sm:text-lg"
              style={{
                backgroundColor: "var(--nos-button-on-primary-bg, #ffffff)",
                color: "var(--nos-button-on-primary-color, var(--nos-color-primary, #3b82f6))",
                borderWidth: 2,
                borderStyle: "solid",
                borderColor: "var(--nos-button-on-primary-border-color, transparent)",
                borderRadius: "var(--nos-button-radius, 8px)",
              }}
              asChild
            >
              <a href={ctaUrl}>{ctaText}</a>
            </Button>
          )
          return order.map((k) => map[k] ?? null)
        })()}
      </div>
      {resolveScrollArrow(content, isHomePage) && <ScrollIndicator />}
    </section>
  )
}
